import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSocketContext } from '../contexts/SocketContext';
import InvitationBanner from '../components/InvitationBanner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Invitation {
  fromUserId: number;
  fromUsername: string;
  gameMode: string;
  wordLength: number;
  useUmlauts: boolean;
}

const MultiplayerLobby: React.FC = () => {
  const [searchParams] = useSearchParams();
  const gameMode = searchParams.get('mode');
  const inviteUserId = searchParams.get('inviteUserId');
  const navigate = useNavigate();
  const { socket } = useSocketContext();

  const [inviteUsername, setInviteUsername] = useState('');
  const [pendingInvitation, setPendingInvitation] = useState<Invitation | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Send invitation if inviteUserId is provided
  useEffect(() => {
    if (inviteUserId && socket && gameMode) {
      // Get user info and send invitation
      fetch(`${API_URL}/friends/list`, {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          const friend = data.friends?.find((f: any) => f.id === parseInt(inviteUserId));
          if (friend && friend.isOnline) {
            setInviteUsername(friend.username);
            // Send invitation automatically
            fetch(`${API_URL}/games/settings`, {
              credentials: 'include'
            })
              .then(res => res.json())
              .then(settings => {
                socket.emit('send-invitation', {
                  username: friend.username,
                  gameMode,
                  wordLength: settings.wordLength || 5,
                  useUmlauts: settings.useUmlauts !== undefined ? settings.useUmlauts : true
                });
              });
          } else {
            setError('Freund nicht gefunden oder nicht online');
          }
        })
        .catch(err => {
          console.error('Error loading friend:', err);
          setError('Fehler beim Laden der Freunde');
        });
    }
  }, [inviteUserId, socket, gameMode]);

  useEffect(() => {
    if (!socket) return;

    socket.on('invitation-received', (data: Invitation) => {
      setPendingInvitation(data);
    });

    socket.on('invitation-sent', (data: { username: string }) => {
      setMessage(`Einladung an ${data.username} gesendet`);
      setInviteUsername('');
    });

    socket.on('invitation-error', (data: { message: string }) => {
      setError(data.message);
    });

    socket.on('invitation-rejected', (data: { username: string }) => {
      setMessage(`Einladung von ${data.username} abgelehnt`);
    });

    // lobby-created is now handled globally in InvitationContext
    socket.on('game-started', (data: any) => {
      navigate(`/game?type=multiplayer&mode=${data.gameMode}&gameId=${data.gameId}`);
    });

    return () => {
      socket.off('invitation-received');
      socket.off('invitation-sent');
      socket.off('invitation-error');
      socket.off('invitation-rejected');
      socket.off('game-started');
    };
  }, [socket, navigate]);

  const handleSendInvitation = () => {
    if (!socket || !inviteUsername.trim()) {
      setError('Bitte gib einen Username ein');
      return;
    }

    setError('');
    setMessage('');

    // Get settings
    fetch(`${API_URL}/games/settings`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(settings => {
        socket.emit('send-invitation', {
          username: inviteUsername.trim(),
          gameMode,
          wordLength: settings.wordLength || 5,
          useUmlauts: settings.useUmlauts !== undefined ? settings.useUmlauts : true
        });
      })
      .catch(err => {
        setError('Fehler beim Senden der Einladung');
      });
  };

  const handleAcceptInvitation = () => {
    if (!socket || !pendingInvitation) {
      console.error('Cannot accept invitation: socket or invitation missing');
      return;
    }
    console.log('Accepting invitation from:', pendingInvitation.fromUserId);
    socket.emit('accept-invitation', {
      fromUserId: pendingInvitation.fromUserId
    });
    setPendingInvitation(null);
  };

  const handleRejectInvitation = () => {
    if (!socket || !pendingInvitation) return;

    socket.emit('reject-invitation', {
      fromUserId: pendingInvitation.fromUserId
    });
    setPendingInvitation(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
            {gameMode === 'alternating' ? 'Gemeinsames Frördle' : '1 vs 1 Frördle'}
          </h1>

          {pendingInvitation && (
            <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-lg mb-6">
              <p className="font-semibold mb-2">
                Einladung von {pendingInvitation.fromUsername}
              </p>
              <p className="text-sm mb-4">
                Modus: {pendingInvitation.gameMode === 'alternating' ? 'Gemeinsames Frördle' : '1 vs 1 Frördle'}
              </p>
              <div className="flex gap-4">
                <button
                  onClick={handleAcceptInvitation}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Annehmen
                </button>
                <button
                  onClick={handleRejectInvitation}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Ablehnen
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Spieler:in einladen (Username)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inviteUsername}
                  onChange={(e) => setInviteUsername(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Username eingeben"
                  onKeyPress={(e) => e.key === 'Enter' && handleSendInvitation()}
                />
                <button
                  onClick={handleSendInvitation}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                >
                  Einladen
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {message && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                {message}
              </div>
            )}

            <button
              onClick={() => navigate('/menu')}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
            >
              Zurück zum Menü
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiplayerLobby;

